// CoreProvider — composes the headless modules (turn engine + card /
// thread / gap stores + session registry + offline layer) into one
// context, and owns the cross-cutting glue that has no natural home in a
// single store: auth + boot, the project SSE subscription, navigation
// (which project / thread is active), and the high-level `sendMessage`
// that ensures a thread, auto-titles it, and drives the turn engine.
//
// Both the mobile entry and (after the Phase 6 migration) the desktop
// entry wrap their app in <CoreProvider> and read state via `useCore()`.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { api, UNAUTHORIZED_EVENT } from '../api'
import type { MeResponse } from '../api'
import { listenToAppResume } from '../native/app_state'
import { claimPendingTemplate } from '../utils/pendingTemplate'

// Single-flight guard for the SEO-template funnel: StrictMode double-runs
// the boot effect, and both runs may reach the template branch. Sharing one
// create-promise at module scope means the project is created exactly once
// and every run lands on the same instance.
let templateCreateInFlight: Promise<Project> | null = null
import { reconcileCards, useCardStore } from './cardStore'
import { useThreadStore } from './threadStore'
import { useTurnEngine } from './turnEngine'
import { useGapStore } from './gapStore'
import { useSessionRegistry } from './sessionRegistry'
import { cardsCache, projectsCache, useOfflineQueue } from './offlineQueue'
import { buildDiscussingGapWire, deriveThreadTitle } from './session'
import type {
  Card,
  ChatMessage,
  ModelChoices,
  Project,
  QueuedSend,
  Thread,
  ThreadUsage,
  WireEvent,
} from './types'

/** Stable empty array so a thread with no bubbles keeps a constant
 *  reference across renders (downstream memos don't churn). */
const EMPTY_MESSAGES: ChatMessage[] = []

export type AuthState = 'loading' | 'in' | 'out'

export interface CoreState {
  authState: AuthState
  me: MeResponse | null
  modelChoices: ModelChoices | null
  projects: Project[]
  activeProjectId: string | null
  activeProject: Project | null
  threads: Thread[]
  activeThreadId: string | null
  activeThread: Thread | null
  cards: Card[]
  gaps: Card[]
  questions: Card[]
  /** Active thread's bubbles — committed history followed by the live
   *  streaming buffer. */
  activeMessages: ChatMessage[]
  /** Whether the active thread has a turn in flight. */
  busy: boolean
  /** Per-thread busy map — the desktop sidebar dots individual rows. */
  busyByThread: Record<string, boolean>
  activeUsage: ThreadUsage | null
  /** A send the user queued while the active thread was busy — shown as a
   *  pending bubble until the running turn drains it. */
  activeQueued: QueuedSend | null
  /** The gap (card id) pinned to the active thread, if any. While a gap
   *  is pinned, every send on that thread is wrapped as a discussing-gap
   *  turn (doc/06 — the desktop's pinned-discussion behavior). */
  pinnedGap: string | null
  /** Full per-thread pinned-gap map — the desktop reads it to dedup a
   *  Discuss click against a thread already pinned to that gap. */
  pinnedGapByThread: Record<string, string>
  anyBusy: boolean
  busyProjects: ReadonlySet<string>
  unreadByThread: Record<string, number>
  unreadProjects: ReadonlySet<string>
  online: boolean
  /** Transient error / notice; null when nothing to show. */
  toast: string | null
}

export interface CoreActions {
  selectProject: (projectId: string) => void
  selectThread: (threadId: string | null) => void
  /** Route to a specific (project, thread) — used by push-notification
   *  taps. Loads the target project first when it isn't the active one. */
  openThread: (projectId: string, threadId: string) => void
  /** Create a thread (optionally titled), select it, and return it — the
   *  desktop's Discuss flow needs the new thread's id to pin a gap. */
  newThread: (title?: string) => Promise<Thread | null>
  /** Create a project (optionally named / packed) and switch to it. */
  newProject: (name?: string, pack?: string) => Promise<Project | null>
  /** Inject an already-created project (e.g. a v3 import) and switch to it. */
  adoptProject: (project: Project) => void
  /** High-level send: ensures a thread exists, auto-titles it from the
   *  first message, then drives the turn engine. */
  sendMessage: (text: string, imageRefs?: string[]) => Promise<void>
  cancelActiveTurn: () => void
  /** Drop the active thread's queued send before the running turn drains it. */
  cancelQueuedSend: () => void
  approveGap: (
    cardId: string,
    input?: { to_kind?: string; title?: string; body?: string },
  ) => Promise<void>
  rejectGap: (cardId: string, reason?: string) => Promise<void>
  /** Discuss a gap — fire a synthetic opener turn so the agent frames the
   *  gap before the back-and-forth (doc/10's discuss mechanic). */
  discussGap: (gapCardId: string) => Promise<void>
  /** Pin a gap to the active thread so every subsequent send wraps as a
   *  discussing-gap turn; `unpinGap` ends it. The desktop pairs `pinGap`
   *  with `discussGap` on a Discuss click. */
  pinGap: (gapCardId: string) => void
  unpinGap: () => void
  /** Clear a gap's pin across every thread — used when a gap is approved
   *  / rejected and the discussing-gap hint must stop firing. */
  unpinGapEverywhere: (gapCardId: string) => void
  renameThread: (threadId: string, title: string) => Promise<void>
  archiveThread: (threadId: string) => Promise<void>
  refreshCards: () => void
  /** 计费：拉 /api/wallet 刷新 me.wallet（turn 完成 / 充值回跳后调用，
   *  驱动余额 chip / 账单页实时更新）。best-effort。 */
  refreshWallet: () => void
  /** Optimistically transform the card list — desktop edits apply here
   *  for instant feedback before the SSE / refetch round-trip confirms. */
  patchCards: (updater: (prev: Card[]) => Card[]) => void
  /** Optimistic local patch of one thread / project — desktop metadata
   *  edits (rename, pack, model preference) apply here for instant feel. */
  updateThread: (threadId: string, patch: (t: Thread) => Thread) => void
  updateProject: (projectId: string, patch: (p: Project) => Project) => void
  /** Subscribe to raw project-SSE wire events — the desktop shell's hook
   *  for the card-highlight / toast / stale-doc reactions core skips.
   *  Returns an unsubscribe. */
  subscribeWireEvents: (fn: (evt: WireEvent) => void) => () => void
  dismissToast: () => void
  /** Bubbles for any thread (committed + streaming) — for screens that
   *  render a thread other than the active one. */
  messagesFor: (threadId: string) => ChatMessage[]
  busyFor: (threadId: string) => boolean
}

export type CoreContextValue = CoreState & CoreActions

const CoreContext = createContext<CoreContextValue | null>(null)

export function useCore(): CoreContextValue {
  const ctx = useContext(CoreContext)
  if (!ctx) throw new Error('useCore must be used within <CoreProvider>')
  return ctx
}

/**
 * Subscribe to raw project-SSE wire events. The desktop shell uses this
 * for its card-highlight / toast / stale-doc / frames-revision reactions;
 * core's own stores are already kept fresh by the same events, so a
 * subscriber only layers on view-specific effects. The handler is held
 * in a ref so it can close over fresh state without re-subscribing.
 */
export function useCoreWireEvents(handler: (evt: WireEvent) => void): void {
  const core = useCore()
  const ref = useRef(handler)
  ref.current = handler
  useEffect(
    () => core.subscribeWireEvents((evt) => ref.current(evt)),
    [core.subscribeWireEvents],
  )
}

export function CoreProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [me, setMe] = useState<MeResponse | null>(null)
  const [modelChoices, setModelChoices] = useState<ModelChoices | null>(null)
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const [activeThreadId, setActiveThreadIdState] = useState<string | null>(null)
  // Per-project last-viewed thread, seeded from /api/me; restored on a
  // project switch so the user lands on the exact thread they left.
  const [projectViews, setProjectViews] = useState<
    Record<string, string | null>
  >({})
  const [toast, setToast] = useState<string | null>(null)
  // Bumped when the app returns from the background. iOS suspends the
  // SSE connections while backgrounded; incrementing this re-runs the
  // project SSE + active-thread effects so they reconnect with fresh
  // EventSources and re-pull anything missed.
  const [resumeNonce, setResumeNonce] = useState(0)
  // Per-thread pinned gap. While a thread has an entry, every send on it
  // is wrapped as a discussing-gap turn (see `sendMessage`).
  const [pinnedGapByThread, setPinnedGapByThread] = useState<
    Record<string, string>
  >({})

  // Refs for values read inside async callbacks / the SSE handler, which
  // must not close over a stale render.
  const activeProjectIdRef = useRef(activeProjectId)
  activeProjectIdRef.current = activeProjectId
  const activeThreadIdRef = useRef(activeThreadId)
  activeThreadIdRef.current = activeThreadId
  const projectViewsRef = useRef(projectViews)
  projectViewsRef.current = projectViews
  const pinnedGapByThreadRef = useRef(pinnedGapByThread)
  pinnedGapByThreadRef.current = pinnedGapByThread

  const showToast = (msg: string) => {
    setToast(msg)
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 4500)
  }

  const cardStore = useCardStore(activeProjectId)
  const threadStore = useThreadStore(activeProjectId)
  const gapStore = useGapStore(cardStore.cards, activeProjectId)
  const offline = useOfflineQueue()

  // Turn-finished glue: a turn may have written cards (refetch), and the
  // thread just gained content (unread bookkeeping). A fresh closure each
  // render is fine — the turn engine ref-mirrors its callbacks.
  const handleTurnFinished = (threadId: string, projectId: string) => {
    void cardStore.refetch(projectId)
    const now = new Date().toISOString()
    const viewing = activeThreadIdRef.current === threadId
    threadStore.updateThread(threadId, (t) => ({
      ...t,
      last_message_at: now,
      updated_at: now,
      last_read_at: viewing ? now : t.last_read_at,
    }))
    if (viewing) api.markThreadRead(threadId).catch(() => {})
  }

  const turnEngine = useTurnEngine({
    onTurnFinished: handleTurnFinished,
    onError: showToast,
  })

  const sessionRegistry = useSessionRegistry({
    busyByThread: turnEngine.busyByThread,
    threads: threadStore.threads,
    projects: threadStore.projects,
    activeThreadId,
    activeProjectId,
  })

  // ─── Navigation + data loading ──────────────────────────────────────

  // Load (or reload) a project's threads + cards. Paints an instant
  // snapshot from the IndexedDB cache, then revalidates from the network.
  const loadProject = async (
    project: Project,
    lastThreadId: string | null,
  ) => {
    setActiveProjectId(project.id)
    setActiveThreadIdState(null)
    try {
      const cached = await cardsCache.read(project.id)
      if (cached && activeProjectIdRef.current === project.id) {
        cardStore.setCards(cached)
      }
    } catch {
      /* cache miss / unavailable — the fetch below covers it */
    }
    try {
      const [threads, cards] = await Promise.all([
        api.listThreads(project.id),
        cardStore.refetch(project.id),
      ])
      // A rapid A→B→A switch can land an older fetch after the user moved
      // on — never let it clobber the visible project.
      if (activeProjectIdRef.current !== project.id) return
      threadStore.setThreads(threads)
      void cardsCache.write(project.id, cards)
      // Restore the last thread ONLY if it still exists — never guess.
      if (lastThreadId && threads.some((t) => t.id === lastThreadId)) {
        setActiveThreadIdState(lastThreadId)
      }
    } catch (e) {
      if (activeProjectIdRef.current === project.id) {
        showToast(`load project failed: ${(e as Error).message}`)
      }
    }
  }
  const loadProjectRef = useRef(loadProject)
  loadProjectRef.current = loadProject

  const selectThread = (threadId: string | null) => {
    setActiveThreadIdState(threadId)
    // Eagerly mirror into the ref so a `pinGap` / `discussGap` fired
    // synchronously right after `newThread` targets the just-selected
    // thread, not the one from the previous render.
    activeThreadIdRef.current = threadId
    const pid = activeProjectIdRef.current
    if (threadId && pid) {
      threadStore.markRead(threadId)
      setProjectViews((prev) => ({ ...prev, [pid]: threadId }))
      api.setLastThread(pid, threadId).catch(() => {})
    }
  }

  const selectProject = (projectId: string) => {
    if (projectId === activeProjectIdRef.current) return
    const p = threadStore.projects.find((x) => x.id === projectId)
    if (p) void loadProject(p, projectViewsRef.current[projectId] ?? null)
  }

  const openThread = (projectId: string, threadId: string) => {
    if (projectId === activeProjectIdRef.current) {
      selectThread(threadId)
      return
    }
    // loadProject restores its `lastThreadId` arg once the project's
    // threads are in — so the target thread lands selected.
    const p = threadStore.projects.find((x) => x.id === projectId)
    if (p) void loadProject(p, threadId)
  }

  // Inject an already-created project into the list and switch to it —
  // shared by `newProject` and the desktop's v3-import flow.
  const adoptProject = (p: Project) => {
    threadStore.setProjects([...threadStore.projects, p])
    void loadProject(p, null)
  }

  const newProject = async (
    name?: string,
    pack?: string,
  ): Promise<Project | null> => {
    try {
      const p = await api.createProject(name ?? '新项目', pack)
      adoptProject(p)
      return p
    } catch (e) {
      showToast(`新建项目失败: ${(e as Error).message}`)
      return null
    }
  }

  const newThread = async (title?: string): Promise<Thread | null> => {
    const pid = activeProjectIdRef.current
    if (!pid) return null
    try {
      const t = await threadStore.createThread(pid, title ?? null)
      turnEngine.seedThread(t.id)
      selectThread(t.id)
      return t
    } catch (e) {
      showToast(`new thread failed: ${(e as Error).message}`)
      return null
    }
  }

  const sendMessage = async (text: string, imageRefs: string[] = []) => {
    const pid = activeProjectIdRef.current
    if (!pid || !text.trim()) return
    let tid = activeThreadIdRef.current
    let created: Thread | null = null
    if (!tid) {
      try {
        created = await threadStore.createThread(pid)
        turnEngine.seedThread(created.id)
        tid = created.id
        selectThread(created.id)
      } catch (e) {
        showToast(`new thread failed: ${(e as Error).message}`)
        return
      }
    }
    // Auto-title an untitled thread from its first message.
    const thread =
      created ?? threadStore.threads.find((t) => t.id === tid) ?? null
    if (thread && !(thread.title ?? '').trim()) {
      const title = deriveThreadTitle(text)
      if (title) void threadStore.renameThread(tid, title)
    }
    // A thread with a pinned gap wraps every send as a discussing-gap
    // turn — the chat bubble stays the user's text, only the wire differs.
    let wireText = text
    const pinnedGapId = pinnedGapByThreadRef.current[tid]
    if (pinnedGapId) {
      const gap = cardStore.cards.find((c) => c.card_id === pinnedGapId)
      if (gap) wireText = buildDiscussingGapWire(gap, cardStore.cards, text)
    }
    await turnEngine.send(tid, pid, text, wireText, imageRefs)
  }

  const cancelActiveTurn = () => {
    const tid = activeThreadIdRef.current
    if (tid) void turnEngine.cancel(tid)
  }

  const cancelQueuedSend = () => {
    const tid = activeThreadIdRef.current
    if (tid) turnEngine.cancelQueued(tid)
  }

  const discussGap = async (gapCardId: string) => {
    const pid = activeProjectIdRef.current
    const tid = activeThreadIdRef.current
    if (!pid || !tid) return
    const gap = cardStore.cards.find((c) => c.card_id === gapCardId)
    if (!gap) return
    // Opener turn — empty displayText (no user bubble); the wire payload
    // carries the gap's full framing context.
    const wire = buildDiscussingGapWire(gap, cardStore.cards, '', {
      opener: true,
    })
    await turnEngine.send(tid, pid, '', wire, [])
  }

  const pinGap = (gapCardId: string) => {
    const tid = activeThreadIdRef.current
    if (tid) {
      setPinnedGapByThread((prev) => ({ ...prev, [tid]: gapCardId }))
    }
  }

  const unpinGap = () => {
    const tid = activeThreadIdRef.current
    if (!tid) return
    setPinnedGapByThread((prev) => {
      if (!(tid in prev)) return prev
      const rest = { ...prev }
      delete rest[tid]
      return rest
    })
  }

  const unpinGapEverywhere = (gapCardId: string) => {
    setPinnedGapByThread((prev) => {
      let changed = false
      const next: Record<string, string> = {}
      for (const [tid, gid] of Object.entries(prev)) {
        if (gid === gapCardId) {
          changed = true
          continue
        }
        next[tid] = gid
      }
      return changed ? next : prev
    })
  }

  // ─── Boot ───────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false
    void (async () => {
      let meRes: MeResponse
      try {
        meRes = await api.me()
      } catch (e) {
        // 401 → UNAUTHORIZED_EVENT already flipped authState via the
        // listener below. Any other error means the server is unreachable.
        if ((e as Error).message !== 'UNAUTHORIZED') setAuthState('out')
        return
      }
      if (cancelled) return
      setMe(meRes)
      setAuthState('in')
      setProjectViews(meRes.project_views)
      api.getModelChoices().then(setModelChoices).catch(() => {})
      // Instant project list from cache, then the authoritative fetch.
      projectsCache
        .read()
        .then((cached) => {
          if (!cancelled && cached && cached.length > 0) {
            threadStore.setProjects(cached)
          }
        })
        .catch(() => {})
      try {
        // SEO-template funnel: a marketing CTA stashed a template before
        // the login round-trip. Create the seeded project now and land
        // the user inside it — that project IS what they came for. Claim
        // + create are both single-flight so StrictMode's duplicate boot
        // run converges on the same project instead of racing this one
        // with the default last-project path.
        const pending = claimPendingTemplate()
        if (pending) {
          try {
            templateCreateInFlight ??= api.createProjectFromTemplate(
              pending.slug,
              pending.lang,
            )
            const p = await templateCreateInFlight
            if (cancelled) return
            // Next boot should also land here, whatever races today.
            api.setLastProject(p.id).catch(() => {})
            const ps = await api.listProjects()
            if (cancelled) return
            threadStore.setProjects(ps)
            void projectsCache.write(ps)
            await loadProjectRef.current(p, null)
            return
          } catch {
            if (cancelled) return
            // Unknown slug / transient failure — fall through to the
            // normal boot path; the visitor still gets the app.
          }
        }
        const ps = await api.listProjects()
        if (cancelled) return
        threadStore.setProjects(ps)
        void projectsCache.write(ps)
        if (ps.length === 0) {
          showToast('no project — backend bootstrap may have failed')
          return
        }
        const initial =
          ps.find((p) => p.id === meRes.last_project_id) ?? ps[0]
        await loadProjectRef.current(
          initial,
          meRes.project_views[initial.id] ?? null,
        )
      } catch (e) {
        if ((e as Error).message !== 'UNAUTHORIZED') {
          showToast(`load failed: ${(e as Error).message}`)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A 401 from any /api/* call → bounce to the login screen.
  useEffect(() => {
    const onUnauth = () => {
      setAuthState('out')
      setMe(null)
      // Drop the active project + thread so the SSE useEffect's cleanup
      // fires and tears down the EventSource. Otherwise an expired
      // cookie leaves the subscription retrying against 401 forever —
      // a sustained background CPU cost while the user is at the login
      // page (or stepped away overnight).
      setActiveProjectId(null)
      setActiveThreadIdState(null)
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauth)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauth)
  }, [])

  // ─── Project SSE ────────────────────────────────────────────────────

  // Stable handle on the stores so the SSE subscription keys only on the
  // project id, not on every render's fresh store objects.
  const sseRef = useRef({ cardStore, threadStore, turnEngine })
  sseRef.current = { cardStore, threadStore, turnEngine }

  // View-layer subscribers to the raw project SSE stream. The desktop
  // shell reacts to card_written / documents_stale / frames_changed for
  // its highlight flashes, toasts, and stale-doc badge — none of which
  // are core's concern (core just keeps the stores fresh). Mobile
  // registers nothing here.
  const wireListenersRef = useRef(new Set<(evt: WireEvent) => void>())
  const subscribeWireEvents = useCallback(
    (fn: (evt: WireEvent) => void): (() => void) => {
      wireListenersRef.current.add(fn)
      return () => {
        wireListenersRef.current.delete(fn)
      }
    },
    [],
  )

  useEffect(() => {
    if (!activeProjectId) return
    const pid = activeProjectId
    const onEvent = (evt: WireEvent) => {
      const s = sseRef.current
      switch (evt.type) {
        case 'threads_changed':
          void s.threadStore.refetchThreads(pid)
          break
        case 'project_changed':
          void s.threadStore.refetchProjects()
          break
        case 'thread_turn_started':
          s.turnEngine.noteTurnStarted(evt.thread_id, evt.turn_id)
          break
        case 'thread_turn_ended':
          s.turnEngine.noteTurnEnded(evt.thread_id, evt.turn_id)
          // 计费：turn 结束、扣费已落账 → 刷新钱包余额，驱动左下余额 chip
          // 与账单页实时更新（不必刷新页面）。best-effort。
          void api.getWallet().then(
            (w) => setMe((p) => (p ? { ...p, wallet: w } : p)),
            () => {},
          )
          break
        case 'documents_stale':
        case 'frames_changed':
          // No documents / frames surface in the rebuilt mobile app yet.
          break
        default:
          // card_written / card_updated / card_deleted / cards_marked_stale
          void s.cardStore.refetch(pid)
      }
      // Fan the raw event out to view-layer subscribers (the desktop
      // shell) after core's own stores have been told to refresh.
      for (const fn of wireListenersRef.current) fn(evt)
    }
    // EventSource doesn't replay events missed across a reconnect, so
    // every (re)open does a full card recover.
    return api.events(pid, onEvent, () => {
      void sseRef.current.cardStore.refetch(pid)
    })
  }, [activeProjectId, resumeNonce])

  // Returning from the background: iOS has likely dropped the SSE
  // connections. Re-pull the active project's data immediately, then
  // bump resumeNonce so the SSE + active-thread effects rebuild their
  // EventSources (a stale connection silently stops delivering events).
  useEffect(() => {
    return listenToAppResume(() => {
      const pid = activeProjectIdRef.current
      if (pid) {
        void sseRef.current.cardStore.refetch(pid)
        void sseRef.current.threadStore.refetchThreads(pid)
      }
      setResumeNonce((n) => n + 1)
    })
  }, [])

  // ─── Active thread: load history + reattach to an in-flight turn ────

  const { loadHistory, reattach } = turnEngine
  useEffect(() => {
    if (!activeThreadId) return
    const pid = activeProjectIdRef.current
    if (!pid) return
    void loadHistory(activeThreadId)
    return reattach(activeThreadId, pid)
  }, [activeThreadId, loadHistory, reattach, resumeNonce])

  // ─── Derived view ───────────────────────────────────────────────────

  const activeProject = useMemo(
    () => threadStore.projects.find((p) => p.id === activeProjectId) ?? null,
    [threadStore.projects, activeProjectId],
  )
  const activeThread = useMemo(
    () => threadStore.threads.find((t) => t.id === activeThreadId) ?? null,
    [threadStore.threads, activeThreadId],
  )
  const activeMessages = useMemo(() => {
    if (!activeThreadId) return EMPTY_MESSAGES
    const committed = turnEngine.messagesByThread[activeThreadId] ?? EMPTY_MESSAGES
    const streaming = turnEngine.streamingByThread[activeThreadId]
    return streaming && streaming.length > 0
      ? [...committed, ...streaming]
      : committed
  }, [activeThreadId, turnEngine.messagesByThread, turnEngine.streamingByThread])

  const messagesFor = useMemo(() => {
    return (threadId: string): ChatMessage[] => {
      const committed = turnEngine.messagesByThread[threadId] ?? EMPTY_MESSAGES
      const streaming = turnEngine.streamingByThread[threadId]
      return streaming && streaming.length > 0
        ? [...committed, ...streaming]
        : committed
    }
  }, [turnEngine.messagesByThread, turnEngine.streamingByThread])

  const busy = activeThreadId
    ? !!turnEngine.busyByThread[activeThreadId]
    : false
  const activeUsage = activeThreadId
    ? turnEngine.usageByThread[activeThreadId] ?? null
    : null
  const activeQueued = activeThreadId
    ? turnEngine.queuedSendByThread[activeThreadId] ?? null
    : null
  const pinnedGap = activeThreadId
    ? pinnedGapByThread[activeThreadId] ?? null
    : null

  const value: CoreContextValue = {
    authState,
    me,
    modelChoices,
    projects: threadStore.projects,
    activeProjectId,
    activeProject,
    threads: threadStore.threads,
    activeThreadId,
    activeThread,
    cards: cardStore.cards,
    gaps: gapStore.gaps,
    questions: gapStore.questions,
    activeMessages,
    busy,
    busyByThread: turnEngine.busyByThread,
    activeUsage,
    activeQueued,
    pinnedGap,
    pinnedGapByThread,
    anyBusy: sessionRegistry.anyBusy,
    busyProjects: sessionRegistry.busyProjects,
    unreadByThread: sessionRegistry.unreadByThread,
    unreadProjects: sessionRegistry.unreadProjects,
    online: offline.online,
    toast,
    selectProject,
    selectThread,
    openThread,
    newThread,
    newProject,
    adoptProject,
    sendMessage,
    cancelActiveTurn,
    cancelQueuedSend,
    approveGap: gapStore.approveGap,
    rejectGap: gapStore.rejectGap,
    discussGap,
    pinGap,
    unpinGap,
    unpinGapEverywhere,
    renameThread: threadStore.renameThread,
    archiveThread: threadStore.archiveThread,
    refreshCards: () => {
      const pid = activeProjectIdRef.current
      if (pid) void cardStore.refetch(pid)
    },
    refreshWallet: () => {
      void api.getWallet().then(
        (w) => setMe((p) => (p ? { ...p, wallet: w } : p)),
        () => {},
      )
    },
    patchCards: cardStore.patchCards,
    updateThread: threadStore.updateThread,
    updateProject: threadStore.updateProject,
    subscribeWireEvents,
    dismissToast: () => setToast(null),
    messagesFor,
    busyFor: (threadId: string) => !!turnEngine.busyByThread[threadId],
  }

  return <CoreContext.Provider value={value}>{children}</CoreContext.Provider>
}

// `reconcileCards` is also used by the desktop migration (Phase 6); keep
// it reachable through the core barrel.
export { reconcileCards }
